import { useEffect, useState } from "react";
import useUiContext from "@/custolink-hooks/useUiContext";
import {
  AssetFrontendCmpFormApi,
  AssetFrontendCmpInquiryApi,
  AssetFrontendCmpInquiryApi$CurrentInquiry,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import { useQuery } from "@tanstack/react-query";
import Loading from "@/components/shared/Loading";
import RdaLoading from "@/custolink-views/asset_frontend/redesigns/RdaLoading";
import CustomfieldsForm from "@/custolink-views/asset_frontend/asset_view/asset_components/CustomfieldsForm";
import { useTranslation } from "react-i18next";
import { AssetViewDetails } from "@/custolink-views/asset_frontend/asset_view/useAssetViewFacade";
import {
  InfoNote,
  PrimaryButton,
  SecondaryButton,
} from "@/custolink-views/asset_frontend/redesigns/formKit";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";

// Shared with the classic design (AssetViewClassic renders this same component), so the
// loading spinner follows the asset-frontend switch; the rest of this file was already
// redesigned unconditionally by an earlier batch.
export default function Inquiry(p: {
  componentId: number;
  assetView: AssetViewDetails;
  onDone: () => void;
}) {
  const uic = useUiContext();
  const { t } = useTranslation();
  const newDesign = useAssetFrontendDesign();

  const [details, setDetails] =
    useState<AssetFrontendCmpInquiryApi$CurrentInquiry | null>(null);
  const [mode, setMode] = useState<"list" | "new" | "details" | null>(null);

  const queryInfo = useQuery({
    queryFn: () => {
      return AssetFrontendCmpInquiryApi.getCurrentEnquiries(
        uic._orgId,
        p.assetView.urlParamsForRequests.viewId,
        p.assetView.urlParamsForRequests.viewValue,
        p.assetView.urlParamsForRequests.assetId,
      );
    },
    queryKey: [
      "AssetFrontendCmpInquiryApi",
      AssetFrontendCmpInquiryApi.getCurrentEnquiries.name,
      uic._orgId + "_" + p.assetView.urlParamsForRequests.keyString,
    ],
  });

  const queryFields = useQuery({
    queryFn: () =>
      AssetFrontendCmpFormApi.getCustomfields(
        uic.organisationContext.organisationId,
        p.componentId,
      ),
    queryKey: [
      "AssetFrontendCmpFormApi",
      AssetFrontendCmpFormApi.getCustomfields.name,
      uic.organisationContext.organisationId,
      p.componentId,
    ],
  });

  useEffect(() => {
    if (queryInfo.data != null) {
      if (queryInfo.data?.enquiries?.length == 0) {
        setMode("new");
      } else {
        setMode("list");
      }
    }
  }, [queryInfo.data]);

  if (mode == null || queryInfo.isPending || queryFields.isPending) {
    return newDesign ? <RdaLoading /> : <Loading loading={true} />;
  }

  if (mode == "list") {
    return (
      <div className="space-y-3">
        <InfoNote>{t("component.inquiry.list.hasOneOrMoreAlready")}</InfoNote>

        <div className="flex flex-col gap-px">
          {queryInfo?.data?.enquiries?.map((e) => (
            <div
              key={e.id}
              className="flex items-center gap-3 bg-[var(--rda-surface)] px-4 py-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-[var(--rda-muted)]">
                  {e.createdPrettyDate}
                </span>
                {e.values?.length > 0 && (
                  <span className="block truncate text-sm font-medium leading-tight tracking-[-0.01em]">
                    {truncate(e.summary, 40)}
                  </span>
                )}
              </span>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setDetails(e);
                  setMode("details");
                }}
              >
                {t("component.inquiry.list.action.view")}
              </SecondaryButton>
            </div>
          ))}
        </div>

        <PrimaryButton type="button" onClick={() => setMode("new")}>
          {t("component.inquiry.list.action.newInquiry")}
        </PrimaryButton>
      </div>
    );
  }

  if (mode == "details" && details != null) {
    return (
      <div className="space-y-3">
        <h4 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--rda-muted)]">
          {t("component.inquiry.details.inquiryCreatedDate")} {details.createdPrettyDate}
        </h4>
        <div className="flex flex-col gap-px">
          {details.values.map((v) => (
            <div key={v.name} className="flex gap-3 bg-[var(--rda-surface)] px-4 py-3">
              <span className="w-1/3 shrink-0 text-[11px] font-bold uppercase leading-snug tracking-[0.1em] text-[var(--rda-muted)]">
                {v.name}
              </span>
              <span className="flex-1 text-sm leading-snug">{v.value}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <SecondaryButton type="button" onClick={() => setMode("list")}>
            {t("component.inquiry.details.back")}
          </SecondaryButton>
        </div>
      </div>
    );
  }

  if (mode == "new") {
    return (
      <CustomfieldsForm
        submitText={t("component.inquiry.submitText")}
        topAlert={t("component.inquiry.alert.pleaseRegisterProductBelow")}
        assetViewDetails={p.assetView}
        componentId={p.componentId}
        fields={queryFields.data?.fields ?? []}
        i18nprefix={queryFields.data?.i18nprefix ?? ""}
        onDone={p.onDone}
      />
    );
  }

  return <></>;
}

function truncate(str: string, length: number) {
  return str.length > length + 2 ? str.substring(0, length) + "..." : str;
}
